import React, { useState, useRef, useEffect, useImperativeHandle } from 'react';
import deepmerge from 'deepmerge';
import { Deferred, PlaybackEvent, PlaybackEventEmitter } from '@annunciate/core';
var State;
(function (State) {
    State["none"] = "none";
    State["ended"] = "ended";
    State["playing"] = "playing";
    State["pause"] = "pause";
    State["loading"] = "loading";
    State["loaded"] = "loaded";
    State["buffering"] = "buffering";
})(State || (State = {}));
const DEFAULT_PLAYER_CONFIG = {
    manifest: {
        hls: {
            sequenceMode: false,
            ignoreManifestTimestampsInSegmentsMode: true,
        },
    },
    abr: {
        defaultBandwidthEstimate: 5e6,
    },
};
const CONTROLS = [
    'play_pause',
    'time_and_duration',
    'spacer',
    'mute',
    'volume',
    'fullscreen',
    'overflow_menu',
    'airplay',
];
export const ShakaPlayer = React.forwardRef(({ src, initialPosition, config, className, displayControls = true, onTrackEnded = () => { }, onTrackPlaying = () => { }, onTrackPaused = () => { }, onTrackLoadRequested = () => { }, onTrackLoading = () => { }, onTrackLoaded = () => { }, onTrackBuffering = () => { }, ...rest }, ref) => {
    const uiContainerRef = useRef(null);
    const videoRef = useRef(null);
    const [state, setState] = useState(State.none);
    const [player] = useState(new Deferred());
    const [ui, setUi] = useState(null);
    useEffect(() => {
        let isCancelled = false;
        let p;
        let overlay;
        async function run() {
            if (isCancelled)
                return;
            const shaka = await import('shaka-player/dist/shaka-player.ui');
            if (isCancelled)
                return;
            p = new shaka.Player(videoRef.current);
            p.configure(DEFAULT_PLAYER_CONFIG);
            player.resolve(p);
            if (isCancelled)
                return;
            overlay = new shaka.ui.Overlay(p, uiContainerRef.current, videoRef.current);
            setUi(overlay);
            p.addEventListener('error', (error) => {
                console.error(error.detail);
                PlaybackEventEmitter.emit(PlaybackEvent.RESTART);
            });
            videoRef?.current?.addEventListener('ended', () => setState(State.ended));
            videoRef?.current?.addEventListener('playing', () => setState(State.playing));
            videoRef?.current?.addEventListener('pause', () => setState(State.pause));
            p.addEventListener('loading', () => setState(State.loading));
            p.addEventListener('loaded', () => setState(State.loaded));
            p.addEventListener('buffering', ({ buffering }) => {
                if (buffering === true) {
                    setState(State.buffering);
                }
            });
            p.addEventListener('error', (event) => {
                console.error(event);
                PlaybackEventEmitter.emit(PlaybackEvent.RESTART);
            });
        }
        run();
        return () => {
            isCancelled = true;
            p?.destroy();
            overlay?.destroy();
        };
    }, []);
    useEffect(() => {
        switch (state) {
            case State.ended:
                onTrackEnded(src);
                break;
            case State.playing:
                onTrackPlaying(src, videoRef.current.currentTime);
                break;
            case State.pause:
                onTrackPaused(src);
                break;
            case State.loading:
                onTrackLoading(src);
                break;
            case State.loaded:
                onTrackLoaded(src);
                break;
            case State.buffering:
                onTrackBuffering(src);
                break;
            default:
                break;
        }
    }, [state]);
    useEffect(() => {
        if (config) {
            player.promise.then((p) => {
                p.configure(deepmerge.all([
                    DEFAULT_PLAYER_CONFIG,
                    config,
                ]));
            });
        }
    }, [config]);
    useEffect(() => {
        if (src) {
            player.promise.then(async (p) => {
                let position = 0;
                if (initialPosition && typeof initialPosition === 'function') {
                    position = await initialPosition(src);
                }
                else if (initialPosition) {
                    position = initialPosition;
                }
                onTrackLoadRequested(src, position);
                try {
                    await p.load(src.asset.src, position);
                }
                catch (error) {
                    console.error(error);
                    PlaybackEventEmitter.emit(PlaybackEvent.RESTART);
                }
            });
        }
    }, [src.asset?.src]);
    useEffect(() => {
        if (ui) {
            const config = {
                addSeekBar: displayControls,
                controlPanelElements: displayControls ? CONTROLS : [],
            };
            ui.configure(config);
        }
    }, [
        displayControls,
        !!ui,
    ]);
    useImperativeHandle(ref, () => ({
        play: (seconds) => {
            player.promise.then((p) => {
                videoRef.current?.play();
                if (seconds) {
                    videoRef.current ? videoRef.current.currentTime = seconds : null;
                }
            });
        },
        pause: () => {
            videoRef.current?.pause();
        },
        getPosition: () => {
            return videoRef.current.currentTime;
        },
    }), []);
    return (React.createElement("div", { ref: uiContainerRef, className: className, style: { height: '100%' } },
        React.createElement("video", { ref: videoRef, style: {
                backgroundColor: 'black',
                maxHeight: '100vh',
                width: '100%',
                maxWidth: '100vw',
            }, ...rest })));
});
//# sourceMappingURL=ShakaPlayer.js.map